import { Box, Grid, Stack, Typography, useTheme } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { BlCheckboxInput } from "Forms/Components/BlCheckboxInput";
import { BlFormInput } from "Forms/Components/BlFormInput";
import { BlRadioButtons } from "Forms/Components/BlRadioButtons";
import { StyledLabelAdornment } from "Forms/Components/FormStyles";
import type { StateAidFormType } from "Forms/Schemas/Insurance/stateAidFormSchema";
import {
  getGetSocialSupportQueryKey,
  useGetSocialSupport,
  useSetSocialSupportCalculationMode,
  useUpdateSocialSupport,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import { AmountFrequency, type SocialSupportBody, SocialSupportCalculationMode } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { type FC, useContext, useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { AnalysisDetailContext } from "../Detail/AnalysisDetailContext";
import { StateAidCalculationModal } from "./StateAidCalculationModal";

type Props = {
  readonly clientId?: string;
  readonly analysisId: string;
  readonly versionId: string;
};

export const StateAid: FC<Props> = ({ clientId, analysisId, versionId }) => {
  const queryClient = useQueryClient();

  const { readOnly } = useContext(AnalysisDetailContext);
  const { t } = useResource();

  const { data: stateAidData } = useGetSocialSupport(analysisId, versionId, clientId ?? "", { query: { enabled: !!clientId } });

  const { mutateAsync: changeCalculationMode } = useSetSocialSupportCalculationMode({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetSocialSupportQueryKey(analysisId, versionId, clientId) });
      },
    },
  });

  const { mutateAsync: updateSocialSupport } = useUpdateSocialSupport({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetSocialSupportQueryKey(analysisId, versionId, clientId) });
      },
    },
  });

  const defaultValues: StateAidFormType = {
    socialSupportCalculationMode: stateAidData ? stateAidData.calculationMode : SocialSupportCalculationMode.individual,
    firstDisability: stateAidData?.firstDisability.value ?? ("0" as any),
    secondDisability: stateAidData?.secondDisability.value ?? ("0" as any),
    thirdDisability: stateAidData?.thirdDisability.value ?? ("0" as any),
    widowPension: stateAidData?.widowPension.value ?? ("0" as any),
    orphanPension: stateAidData?.orphanPension.value ?? ("0" as any),
    includeWidowPension: !!stateAidData?.includeWidowPension,
    includeOrphanPension: !!stateAidData?.includeOrphanPension,
  };

  const {
    control,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<StateAidFormType>({
    defaultValues,
  });

  // biome-ignore lint: <Open is required.>
  useEffect(() => {
    reset(defaultValues);
  }, [stateAidData, reset]);

  const onSubmit = async (data: StateAidFormType, updates?: Partial<SocialSupportBody>) => {
    try {
      await updateSocialSupport({
        analysisId,
        versionId,
        clientGuid: clientId || "",
        data: {
          calculationMode: data.socialSupportCalculationMode as SocialSupportCalculationMode,
          includeOrphanPension: data.includeOrphanPension,
          includeWidowPension: data.includeWidowPension,
          firstDisability: {
            value: data.firstDisability,
            currencyCode: "CZK",
            frequency: AmountFrequency.monthly,
          },
          secondDisability: {
            value: data.secondDisability,
            currencyCode: "CZK",
            frequency: AmountFrequency.monthly,
          },
          thirdDisability: {
            value: data.thirdDisability,
            currencyCode: "CZK",
            frequency: AmountFrequency.monthly,
          },
          widowPension: {
            value: data.widowPension,
            currencyCode: "CZK",
            frequency: AmountFrequency.monthly,
          },
          orphanPension: {
            value: data.orphanPension,
            currencyCode: "CZK",
            frequency: AmountFrequency.monthly,
          },
          ...updates,
        },
      });
    } catch {
      toast.error(t(Resources.Common.Error));
    }
  };

  return (
    <Stack spacing={Spacing.XL}>
      <Box display="flex" justifyContent="space-between">
        <Typography variant="subtitle1" fontWeight={600}>
          {t(Resources.AnalysisDetail.Insurance.StateAid)}
        </Typography>
        {!!clientId && <StateAidCalculationModal analysisId={analysisId} versionId={versionId} clientId={clientId} readOnly={readOnly} />}
      </Box>
      <Box>
        <Typography variant="body2" fontWeight={500}>
          {t(Resources.AnalysisDetail.Insurance.StateAidEntryType.Title)}
        </Typography>
      </Box>
      <BlRadioButtons
        control={control}
        disabled={readOnly}
        gridSize={5}
        name="socialSupportCalculationMode"
        codeList={[
          {
            code: SocialSupportCalculationMode.individual,
            name: t(Resources.AnalysisDetail.Insurance.StateAidEntryType.Individual),
          },
          {
            code: SocialSupportCalculationMode.average,
            name: t(Resources.AnalysisDetail.Insurance.StateAidEntryType.Average),
          },
        ]}
        onChange={async (value) => {
          try {
            await changeCalculationMode({
              analysisId,
              versionId,
              clientGuid: clientId || "",
              mode: value as SocialSupportCalculationMode,
            });
          } catch {
            toast.error(t(Resources.Common.Error));
          }
        }}
        errors={errors}
      />
      <Grid container spacing={Spacing.XL}>
        <Grid size={4}>
          <BlFormInput
            control={control}
            name="firstDisability"
            label={t(Resources.AnalysisDetail.Insurance.DisabilityFirstLevel)}
            errors={errors}
            inputEndAdornment={
              <Box sx={{ width: 110 }}>
                <Typography variant="body2" color="secondary">
                  {t(Resources.Common.CZKSuffix)}/{t(Resources.Common.Month)}
                </Typography>
              </Box>
            }
            unmask="typed"
            mask={[
              {
                mask: Number,
                thousandsSeparator: " ",
                scale: 2,
              },
            ]}
            onBlur={() => handleSubmit((data) => onSubmit(data))()}
          />
        </Grid>
        <Grid size={4}>
          <BlFormInput
            control={control}
            name="secondDisability"
            label={t(Resources.AnalysisDetail.Insurance.DisabilitySecondLevel)}
            errors={errors}
            inputEndAdornment={
              <Box sx={{ width: 110 }}>
                <Typography variant="body2" color="secondary">
                  {t(Resources.Common.CZKSuffix)}/{t(Resources.Common.Month)}
                </Typography>
              </Box>
            }
            unmask="typed"
            mask={[
              {
                mask: Number,
                thousandsSeparator: " ",
                scale: 2,
              },
            ]}
            onBlur={() => handleSubmit((data) => onSubmit(data))()}
          />
        </Grid>
        <Grid size={4}>
          <BlFormInput
            control={control}
            name="thirdDisability"
            label={t(Resources.AnalysisDetail.Insurance.DisabilityThirdLevel)}
            errors={errors}
            inputEndAdornment={
              <Box sx={{ width: 110 }}>
                <Typography variant="body2" color="secondary">
                  {t(Resources.Common.CZKSuffix)}/{t(Resources.Common.Month)}
                </Typography>
              </Box>
            }
            unmask="typed"
            mask={[
              {
                mask: Number,
                thousandsSeparator: " ",
                scale: 2,
              },
            ]}
            onBlur={() => handleSubmit((data) => onSubmit(data))()}
          />
        </Grid>
        <Grid size={6}>
          <Box mb={Spacing.XS}>
            <BlCheckboxInput
              control={control}
              disabled={readOnly}
              name="includeWidowPension"
              text={t(Resources.AnalysisDetail.Insurance.WidowPension)}
              errors={errors}
              onClick={() =>
                handleSubmit((data) =>
                  onSubmit(data, {
                    includeWidowPension: !stateAidData?.includeWidowPension,
                  }),
                )()
              }
            />
          </Box>
          <BlFormInput
            control={control}
            name="widowPension"
            errors={errors}
            inputEndAdornment={
              <Box sx={{ width: 110 }}>
                <Typography variant="body2" color="secondary">
                  {t(Resources.Common.CZKSuffix)}/{t(Resources.Common.Month)}
                </Typography>
              </Box>
            }
            unmask="typed"
            mask={[
              {
                mask: Number,
                thousandsSeparator: " ",
                scale: 2,
              },
            ]}
            onBlur={() => handleSubmit((data) => onSubmit(data))()}
          />
        </Grid>
        <Grid size={6}>
          <Box mb={Spacing.XS}>
            <BlCheckboxInput
              control={control}
              disabled={readOnly}
              name="includeOrphanPension"
              text={t(Resources.AnalysisDetail.Insurance.OrphanPension)}
              errors={errors}
              onClick={() => {
                handleSubmit(
                  async (data) =>
                    await onSubmit(data, {
                      includeOrphanPension: !stateAidData?.includeOrphanPension,
                    }),
                )();
              }}
            />
          </Box>
          <BlFormInput
            control={control}
            name="orphanPension"
            errors={errors}
            onBlur={() => handleSubmit((data) => onSubmit(data))()}
            inputEndAdornment={
              <Box sx={{ width: 110 }}>
                <Typography variant="body2" color="secondary">
                  {t(Resources.Common.CZKSuffix)}/{t(Resources.Common.Month)}
                </Typography>
              </Box>
            }
            unmask="typed"
            mask={[
              {
                mask: Number,
                thousandsSeparator: " ",
                scale: 2,
              },
            ]}
          />
        </Grid>
      </Grid>
    </Stack>
  );
};
